import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import { useIntl } from 'react-intl/dist';
import { Modal } from 'app-components';
import { ModalContextType } from '../../../contexts/modalContext';
import { uploadDocumentUsingQRCode } from '../../../actions/dmi/dmiActionCreators';
import { configs } from '../../../constants/configs/configs';
import { useAppDispatch, } from '../../../hooks';
import useQRCodeTimer from '../hooks/useQRCodeTimer';
import ConditionalRendering from '../../../components/common/conditional-rendering/ConditionalRendering';
import Image from '../../../components/common/image/Image';
import { StyledTextCenter, StyledTextPin, StyledMarginBottom, StyledLink, StyledQRCodeContainer } from '../utils/StyledComponents';

interface IQRCodeModalProps extends ModalContextType {
    qrCode: string;
    showRegenerateQRCode: boolean;
    onRegenerateQRCode: (data: any) => void;
    websiteName: string;
    pin: string;
    applicantId: string;
    documentCategory: string;
    documentType: string;
    userId: number;
}

const formatQRCodeTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes === 0 && secs < 60) {
        return (
            <StyledQRCodeContainer>
                <FormattedMessage
                    id="dmi.applicantVerifications.timer.validForSeconds"
                    values={{ seconds: secs }}
                />
            </StyledQRCodeContainer>
        );
    }
    return (
        <StyledQRCodeContainer>
            <FormattedMessage
                id="dmi.applicantVerifications.timer.validForMinutes"
                values={{ minutes, seconds: secs < 10 ? `0${secs}` : secs }}
            />
        </StyledQRCodeContainer>
    );
};

const checkIfQRCodeTextOptional = (intl) => {
    return intl.formatMessage({ id: 'dmi.applicantVerifications.qrCodeTextOptional' }) !== " ";
};

export default function QRCodeModal({ qrCode, showRegenerateQRCode, closeModal, websiteName, pin, applicantId, documentCategory, documentType, userId }: IQRCodeModalProps) {
    const { rawTime, start, reset } = useQRCodeTimer(5);
    const [showRegenerate, setShowRegenerate] = useState(showRegenerateQRCode);
    const [qrCodeState, setQrCodeState] = useState(qrCode);
    const [pinState, setPinState] = useState(pin);
    const [websiteNameState, setWebsiteNameState] = useState(websiteName);
    const dispatch = useAppDispatch();
    const intl = useIntl();

    useEffect(() => {
        start();
    }, [start]);

    useEffect(() => {
        if (rawTime === 0) {
            setShowRegenerate(true);
        }
    }, [rawTime]);

    useEffect(() => {
        setQrCodeState(qrCode);
        setPinState(pin);
        setWebsiteNameState(websiteName);
    }, [qrCode, pin, websiteName]);

    function onClose() {
        closeModal('qrCodeModal');
    }

    const handleRegenerateQRCode = async () => {
        try {
            const params = {
                applicantId: applicantId,
                documentType: documentType,
                documentCategory: documentCategory,
                userId: userId
            };

            dispatch(uploadDocumentUsingQRCode(configs.endpoints.DMI_UPLOAD_DOCUMENT_QR_CODE_ENDPOINT, params, data => {
                if (data) {
                    setQrCodeState(data?.qrCode);
                    setPinState(data?.pin);
                    setWebsiteNameState(data?.websiteName);
                    setShowRegenerate(false);
                    reset();
                }
            }));
        } catch (error) {
            console.error('Failed to regenerate QR code:', error);
        }
    };

    const isQRCodeTextOptional = checkIfQRCodeTextOptional(intl);

    return (
        <Modal id="qrCodeModal">
            <Modal.Header
                id="qrCodeModalHeader"
                titleId="&nbsp;"
                onHide={onClose}
            />
            <Modal.Body id="qrCodeModalBody">
                <StyledTextCenter>
                    <FormattedMessage id="dmi.applicantVerifications.qrCodeText" />
                </StyledTextCenter>
                <ConditionalRendering isTrue={isQRCodeTextOptional}>
                    <StyledTextCenter>
                        <FormattedMessage id="dmi.applicantVerifications.qrCodeTextOptional" />
                    </StyledTextCenter>
                </ConditionalRendering>
                <StyledTextCenter>
                    <FormattedMessage id="dmi.applicantVerifications.qrCodeClick" values={{ websiteName: websiteNameState }} />
                </StyledTextCenter>
                <StyledTextCenter>
                    <FormattedMessage id="dmi.applicantVerifications.qrCodeTxt" />
                </StyledTextCenter>
                <StyledTextCenter>
                    {formatQRCodeTimer(rawTime)}
                </StyledTextCenter>
                <StyledTextCenter>
                    <Image
                        source= {`data:image/png;base64,${qrCodeState}`}
                        desc="QR Code"
                        name="qr_code"
                        isMessageFormattingEnabled={false}
                    />
                </StyledTextCenter>
                <StyledMarginBottom>
                    <StyledTextPin>
                        <FormattedMessage id="dmi.applicantVerifications.pin" values={{ pin: pinState }}/>
                    </StyledTextPin>
                </StyledMarginBottom>
                <ConditionalRendering isTrue={showRegenerate}>
                    <StyledTextCenter>
                        <StyledLink onClick={handleRegenerateQRCode}>
                            <FormattedMessage id="dmi.applicantVerifications.generateQrCode" />
                        </StyledLink>
                    </StyledTextCenter>
                </ConditionalRendering>
            </Modal.Body>
        </Modal>
    );
}